import { Address, PaymentMethodIdEnum } from "@core/types";
import { UseFormSetValue } from "react-hook-form";
import { useCallback, useEffect } from "react";
import { ShippingMethod } from "@core/types";
import { CartFormValues } from "./types";
import useTaxiMasterShippingActions from "../../../providers/address/useTaxiMasterShippingActions";
import { TAXI_MASTER_PAYMENT_METHOD } from "../../../providers/address/taxiMasterShippingUtils";
import { useCustomerAddressesStorageData } from "../../../providers/customerAddressesStorage";
import { useOrderData } from "../../../providers/orderDataStore";

interface UseTaxiShippingProps {
  setValue: UseFormSetValue<CartFormValues>;
  enabledShippingMethods: ShippingMethod[];
}

/**
 * * локальный "управленец формой", который дергает первый хук и меняет состояние интерфейса корзины.
 * * Это связующее звено между главными расчетами и интерфейсом оформления заказа (формой в корзине).

 * * Что делает: Управляет галочками и выбором пользователя на экране. Например, если расчет стоимости прошел успешно, он автоматически включает курьерскую доставку и выбирает нужный способ оплаты.
*  * Ограничения: Автоматически отключает возможность доставки такси, если клиент выбрал оплату наличными или удалил свой адрес.

 */
export const useTaxiShipping = ({ setValue, enabledShippingMethods }: UseTaxiShippingProps) => {
  const { currentShippingAddressId } = useCustomerAddressesStorageData();
  const { paymentMethod } = useOrderData();

  const {
    handleUpdateOrderData: applyTaxiMasterUpdate,
    calculateAndApplyTaxiCost,
    isCalculateTaxiShippingPending,
    isTaxiMasterAvailable,
    isTaxiMasterTurned,
  } = useTaxiMasterShippingActions({ enabledShippingMethods, enableRecalc: false });

  const handleUpdateOrderData = useCallback(
    (params: Parameters<typeof applyTaxiMasterUpdate>[0]) => {
      const isApplied = applyTaxiMasterUpdate(params);

      if (params.isCourierServiceTurned && isApplied) {
        setValue("payment", TAXI_MASTER_PAYMENT_METHOD.title);
        setValue("courierService", true);
        return;
      }

      if (!params.isCourierServiceTurned) {
        setValue("courierService", false);
      }
    },
    [applyTaxiMasterUpdate, setValue],
  );

  const handleCalculateTaxiShipping = useCallback(
    async ({ isTurned, address }: { isTurned: boolean; address: Address | null }) => {
      if (!isTaxiMasterAvailable) {
        setValue("courierService", false);
        return;
      }

      const isApplied = await calculateAndApplyTaxiCost(address, { isTurned });

      if (isTurned && address && isApplied) {
        setValue("payment", TAXI_MASTER_PAYMENT_METHOD.title);
        setValue("courierService", true);
        return;
      }

      setValue("courierService", false);
    },
    [calculateAndApplyTaxiCost, isTaxiMasterAvailable, setValue],
  );

  useEffect(() => {
    if (isCalculateTaxiShippingPending) return;

    if (!isTaxiMasterAvailable) {
      setValue("courierService", false);
      return;
    }

    const isCashPayment = paymentMethod?.type === PaymentMethodIdEnum.cash;
    const shouldDisableCourierService = !currentShippingAddressId || (isCashPayment && isTaxiMasterTurned);

    if (shouldDisableCourierService) {
      setValue("courierService", false);

      if (isTaxiMasterTurned) {
        handleUpdateOrderData({ isCourierServiceTurned: false });
      }

      return;
    }

    setValue("courierService", isTaxiMasterTurned && !isCashPayment);
  }, [
    currentShippingAddressId,
    handleUpdateOrderData,
    isTaxiMasterAvailable,
    isTaxiMasterTurned,
    paymentMethod?.type,
    setValue,
    isCalculateTaxiShippingPending,
  ]);

  return {
    handleUpdateOrderData,
    handleCalculateTaxiShipping,
    isCalculateTaxiShippingPending,
  };
};
